/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Injectable} from '@angular/core';
import {Observable, throwError} from 'rxjs';

import {ApiConfig} from '../../config/api-config';
import {CHUNK_SIZE} from '../../common/constants/resource-constants';
import {AdnApiClientService} from '../../common/controller/adn-api-client.service';
import {SearchApiService} from '../../common/controller/search-api.service';

export type QueryStatsEndpoint = 'stats' | 'targeting' | 'async' | 'asyncTargeting';

@Injectable({providedIn: 'root'})
export class QueryPageApiService {
  constructor(
    private readonly client: AdnApiClientService,
    private readonly searchApi: SearchApiService,
    private readonly apiConfig: ApiConfig
  ) {
  }

  search(search: string, type: string): Observable<any> {
    return this.searchApi.search({
      q: search,
      types: type,
      objectState: 'ACTIVE'
    });
  }

  listTeams(): Observable<any> {
    return this.client.request({
      method: 'GET',
      url: this.apiConfig.getVersionedUri('teams'),
      params: {
        includeHidden: false,
        includeActive: true,
        includeInactive: false,
        orderBy: 'name',
        pageSize: 1001,
        minimal: true
      }
    });
  }

  stats(params: Record<string, any>, endpoint: QueryStatsEndpoint, asyncId?: string): Observable<any> {
    const normalized = this.normalizeStatsParams(params);
    if (!normalized.startDate && normalized.idKey && String(normalized[normalized.idKey] || '').includes(';')) {
      return throwError(() => ({invalidDate: true}));
    }

    const id = endpoint === 'async' ? asyncId : undefined;
    return this.client.request({
      method: 'GET',
      url: this.statsUri(endpoint, id),
      params: this.withoutTransportParams(normalized),
      hasErrorHandler: true
    });
  }

  private normalizeStatsParams(params: Record<string, any>): Record<string, any> {
    const normalized: any = {
      showFilteredBidReasons: true,
      groupBy: CHUNK_SIZE.DAILY.key,
      ...params
    };
    if (normalized.startDate?.toISOString) {
      normalized.startDate = normalized.startDate.toISOString();
    }
    if (normalized.endDate?.toISOString) {
      normalized.endDate = normalized.endDate.toISOString();
    }
    if (normalized.idKey) {
      normalized[normalized.idKey] = normalized.id;
    }
    if (normalized.groupBy) {
      normalized.groupBy = String(normalized.groupBy).toUpperCase();
    } else {
      delete normalized.groupBy;
    }
    normalized.includeChunkRange = true;
    return normalized;
  }

  private withoutTransportParams(params: Record<string, any>): Record<string, any> {
    const requestParams = {...params};
    delete requestParams.id;
    delete requestParams.idKey;
    return requestParams;
  }

  private statsUri(endpoint: QueryStatsEndpoint, asyncId?: string): string {
    let path: string[];
    if (endpoint === 'targeting') {
      path = ['stats', 'targeting'];
    } else if (endpoint === 'asyncTargeting') {
      path = ['stats', 'async', 'targeting'];
    } else if (endpoint === 'async') {
      path = asyncId ? ['stats', 'async', asyncId] : ['stats', 'async'];
    } else {
      path = ['stats'];
    }
    return this.apiConfig.getVersionedUri(path) + '/';
  }
}
